// ProfilesPage.tsx
import { useCallback, useState } from "react";
import { Building2, Hotel, Home, UserCircle, Sparkles, SearchIcon, Loader2, LayoutGrid, List } from "lucide-react";
import { motion } from "framer-motion";
import MyListPage from "../MY_COMPONENTS/MyListPage";
import { MY_ENDPOINTS } from "@raashidh/shared";
import MyHeaderLabel from "@/components/MyHeaderLabel";
import { useAuth } from "@/context/AuthContext";
import { useMyDialog } from "../context/MyDialogProvider";
import ProfileDetails from "./ProfileDetails";
import ProfileCard from "./ProfileCard";
import ProfileCardList from "./ProfileCardList";
import { useNavbarHeight } from "@/hooks/useNavbarHeight";
import CustomListingsPage from "@/MY_COMPONENTS/CustomListingsPage";
import { FaHeart, FaUser } from "react-icons/fa6";
import { FaFemale, FaHeartBroken, FaMale, FaPlus } from "react-icons/fa";
import MapView from "@/MY_COMPONENTS/MapView";
import MyIconButton from "@/components/MyIconButton";
import MyDialogButton from "@/MY_COMPONENTS/MyDialogButton";
import { useNavigate } from "react-router-dom";
import AddProfileForm from "./AddProfileForm";
import { UserAvatars } from "@/components/UserAvatars";
import { useToast } from "@/context/ToastContext";
import ProfileCardSmall from "./ProfileCardSmall";
import ProfileView from "./ProfileView";
import { LoginForm } from "@/AUTH/LoginForm";

export default function ProfilesPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const navbarHeight = useNavbarHeight('nav');
  const { open } = useMyDialog();
  const toast = useToast();

  const userProfileStatus = user?.profile_status;

  // State for tracking reactions (optional)
  const [reactionStates, setReactionStates] = useState<Record<string, any>>({});

  // Handle view details
  const handleViewDetails = useCallback((profile: any) => {
    open({
      content: <ProfileDetails />,
      header: <MyHeaderLabel label={profile.nickname || "Profile Details"} />,
    });
  }, [open]);
  const handleView = useCallback((profile: any) => {
    open({
      content: <ProfileView profile={profile} />,
      header: <MyHeaderLabel label={profile.nickname || "Profile Details"} />,
    });
  }, [open]);

  // Handle like reaction
  const handleLike = useCallback(async (profile: any) => {
    try {
      // Implement your like mutation here
      console.log("Liked profile:", profile.user_id);
      toast.success(`Liked ${profile.nickname || 'profile'}`);
      
      // Update local state
      setReactionStates(prev => ({
        ...prev,
        [profile.user_id]: { ...prev[profile.user_id], liked: !prev[profile.user_id]?.liked }
      }));
    } catch (error) {
      console.error("Error liking profile:", error);
      toast.error("Failed to like profile");
    }
  }, [toast]);

  // Handle super like reaction
  const handleSuperLike = useCallback(async (profile: any) => {
    try {
      console.log("Super liked profile:", profile.user_id);
      toast.success(`Super liked ${profile.nickname || 'profile'}! ✨`);
      
      setReactionStates(prev => ({
        ...prev,
        [profile.user_id]: { ...prev[profile.user_id], superLiked: !prev[profile.user_id]?.superLiked }
      }));
    } catch (error) {
      console.error("Error super liking profile:", error);
      toast.error("Failed to super like profile");
    }
  }, [toast]);

  // Handle save reaction
  const handleSave = useCallback(async (profile: any) => {
    try {
      console.log("Saved profile:", profile.user_id);
      toast.success(`Saved ${profile.nickname || 'profile'} to favorites`);
      
      setReactionStates(prev => ({
        ...prev,
        [profile.user_id]: { ...prev[profile.user_id], saved: !prev[profile.user_id]?.saved }
      }));
    } catch (error) {
      console.error("Error saving profile:", error);
      toast.error("Failed to save profile");
    }
  }, [toast]);

  // Handle message
  const handleMessage = useCallback((profile: any) => {
    // Navigate to chat or open message dialog
    navigate(`/messages/${profile.user_id}`);
  }, [navigate]);

  // Handle share
  const handleShare = useCallback(async (profile: any) => {
    const url = `${window.location.origin}/profile/${profile.user_id}`;
    try {
      if (navigator.share) {
        await navigator.share({
          title: `${profile.nickname}, ${profile.age}`,
          text: `Check out ${profile.nickname}'s profile`,
          url: url,
        });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Profile link copied to clipboard!");
      }
    } catch (err) {
      console.error("Error sharing:", err);
      toast.error("Failed to share profile");
    }
  }, [toast]);

  // Show loading while auth is initializing
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-purple-600 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Loading authentication...</p>
        </div>
      </div>
    );
  }

  return (
    <CustomListingsPage
      endpoint={MY_ENDPOINTS.PROFILES_ENDPOINTS.GET_PROFILES.path}
      method={MY_ENDPOINTS.PROFILES_ENDPOINTS.GET_PROFILES.method}
      queryKey="profiles"
      headerText="Profiles Directory"
      headerSubtext="Connect with professionals, businesses, and individuals from around the world"
      headerIcon={UserCircle}
      headerBadge="Discover Suitable People"
      showHeader
      headerButtons={
        <>
          

          {userProfileStatus == "verified" && (
            <MyIconButton
              link="/profiles/myrequests"
              label="Requests"
              icon={<UserCircle className="w-5 h-5" />}
              iconPosition="left"
              buttonStyle="px-2 py-3 bg-white dark:bg-gray-800 text-purple-600 dark:text-purple-400 rounded-xl font-semibold hover:shadow-lg transition-all"
            />
          )}

          {userProfileStatus == null && (
            <MyDialogButton 
              button={
                <MyIconButton 
                  iconPosition="left" 
                  // icon={<FaPlus className="w-5 h-5" />} 
                  label={user ? "Create My Profile" : "Login to Create Profile"}
                  buttonStyle="px-2 py-3 bg-purple-600 text-white rounded-xl font-semibold hover:bg-purple-700 transition-all"
                />
              }
              dialogContent={user ? <AddProfileForm /> : <LoginForm />} 
            />
          )} 

          {userProfileStatus == "verified" && (
            <MyIconButton
              link="/profiles/myprofile"
              label="MyProfile"
              icon={<UserCircle className="w-5 h-5" />}
              iconPosition="left"
              buttonStyle="px-2 py-3 bg-white dark:bg-gray-800 text-purple-600 dark:text-purple-400 rounded-xl font-semibold hover:shadow-lg transition-all"
            />
          )}
          
    
        </>
      }
      
      searchPlaceholder="Search profiles by name, location, or interests..."
      enableSearch={true}
      searchDebounceMs={500}
      defaultView="grid"
      gridCols={{ mobile: 1, sm: 2, md: 3, lg: 4, xl: 5 }}
      enableFilters={true}
      filterConfig={{
        sortBy: [
          { value: "latest", label: "Latest" },
          { value: "popular", label: "Most Popular" },
          { value: "active", label: "Most Active" }
        ],
        status: [
          { value: "all", label: "All Statuses" },
          { value: "single", label: "Single" },
          { value: "divorced", label: "Divorced" },
          { value: "widowed", label: "Widowed" }
        ],
        gender: [
          { value: "all", label: "All Genders" },
          { value: "m", label: "Male" },
          { value: "f", label: "Female" }
        ]
      }}
      enableCategories={true}
      categoryKey="marital_status"
      categories={[
        { id: "single", label: "Single", icon: FaUser },
        { id: "divorced", label: "Divorced", icon: FaHeartBroken },
        { id: "widowed", label: "Widowed", icon: FaHeart }
      ]}
      categoriesLabel="Relationship Status"
      enableMapView={true}
      navbarHeight={navbarHeight || 64}
      renderItem={(item: any, view: "grid" | "list" | "map") => {
        // Merge reaction states with profile data
        const profileWithReactions = {
          ...item,
          my_reaction_type: reactionStates[item.user_id]?.liked ? 'like' :
                           reactionStates[item.user_id]?.saved ? 'save' :
                           reactionStates[item.user_id]?.superLiked ? 'super_like' :
                           item.my_reaction_type
        };

        switch (view) {
          case "grid":
            return (
              <motion.div 
                whileHover={{ y: -4 }} 
                transition={{ duration: 0.2 }}
                className="h-full"
              >
                <ProfileCardSmall
                  key={item.user_id}
                  profile={item}
                  onLike={handleLike}
                  onSuperLike={handleSuperLike}
                  onSave={handleSave}
                  onMessage={handleMessage}
                  onShare={handleShare}
                  onClick={() => handleView(item)}
                  size="sm"
                  showActions={true}
                />
                {/* <ProfileCard
                  key={item.user_id}
                  data={profileWithReactions}
                  onReactionChange={(userId, reactionType, isAdding) => {
                    console.log('Reaction changed:', userId, reactionType, isAdding);
                    // Update reaction states based on the reaction type
                    setReactionStates(prev => ({
                      ...prev,
                      [userId]: {
                        ...prev[userId],
                        liked: reactionType === 'like' ? isAdding : prev[userId]?.liked,
                        saved: reactionType === 'save' ? isAdding : prev[userId]?.saved,
                        superLiked: reactionType === 'super_like' ? isAdding : prev[userId]?.superLiked
                      }
                    }));
                  }}
                /> */}
              </motion.div>
            );
          
          case "list":
            return (
              <motion.div 
                whileHover={{ y: -2 }} 
                transition={{ duration: 0.2 }}
                className="mb-4"
              >
                <ProfileCardList
                  key={item.user_id}
                  profile={profileWithReactions}
                  onViewDetails={handleView}
                  onLike={handleLike}
                  onSuperLike={handleSuperLike}
                  onSave={handleSave}
                  onMessage={handleMessage}
                  // onShare={handleShare}
                />
              </motion.div>
            );
          
          default:
            return null;
        }
      }}
      renderMapView={(listings, onListingClick) => {
        return (
          <div className="h-[600px] rounded-xl overflow-hidden">
            <MapView
              items={listings.map(listing => ({
                id: listing.user_id,
                title: listing.nickname || "Profile",
                lat: listing.public_location_lat ?? 4.175666,
                lng: listing.public_location_lon ?? 73.508889,
                image: listing.photos?.[0],
                extra: listing,
                label: `${listing.gender === 'm' ? '👨' : '👩'} ${listing.nickname}, ${listing.age}`
              }))}
              height="100%"
              zoom={13}
              center={listings[0] ? [listings[0].public_location_lat, listings[0].public_location_lon] : [4.175278, 73.508889]}
              renderPopup={(item) => (
                <div className="p-3 max-w-xs">
                  <div className="flex items-center gap-2 mb-2">
                    <img 
                      src={item.image || '/default-avatar.png'} 
                      alt={item.title}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <h3 className="font-semibold text-sm">{item.title}</h3>
                      <p className="text-xs text-gray-500">{item.label}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => onListingClick?.(item.extra)}
                    className="w-full mt-2 px-3 py-1 bg-purple-600 text-white text-xs rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    View Profile
                  </button>
                </div>
              )}
            />
          </div>
        );
      }}
      onSearch={(searchTerm) => {
        console.log('Searching for:', searchTerm);
        // You can add analytics or additional search handling here
      }}
    />
  );
}